import { createCollectorProxyBrowserClient } from "@qws-org/collector-browser-sdk";
import { useEffect } from "react";
import type { Metric } from "web-vitals";
import { onCLS, onFCP, onINP, onLCP, onTTFB } from "web-vitals";

const SCENARIO_ID = "site.web-vitals";
/** Старт сбора — когда браузер простаивает, но не позже этого срока. */
const START_TIMEOUT_MS = 3_000;

type WebVitalsReporterProps = {
  /** Публичный URL collector-proxy-service (переиспользуем metricsService из GTM-конфига). */
  collectorProxyUrl?: string;
  /** true для ботов/синтетических аудитов/локального запуска — RUM их не должен учитывать. */
  skipReporting: boolean;
  /** dev|prod — выводится из hostname запроса, см. root.tsx. */
  siteEnvironment: string;
  /** Идентификатор бренда/сайта — hostname запроса, см. root.tsx. */
  siteBrand: string;
};

/**
 * UUID v4 без `crypto.randomUUID`: его нет вне secure context (http на
 * не-localhost, открытие по IP) и в старых браузерах/WebView (Safari < 15.4,
 * Chrome < 92). Раньше вызов падал прямо в useEffect, и ErrorBoundary
 * корневого роута заменял всю страницу, включая форму регистрации.
 */
const uuidV4 = (): string => {
  const cryptoApi = globalThis.crypto as Crypto | undefined;
  if (typeof cryptoApi?.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof cryptoApi?.getRandomValues === "function") {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const runWhenIdle = (task: () => void): void => {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(task, { timeout: START_TIMEOUT_MS });
    return;
  }
  window.setTimeout(task, 0);
};

/**
 * Слушатели web-vitals нельзя снять, поэтому сбор запускается один раз на
 * документ: повторный mount (StrictMode, ремоунт Layout при ошибке роута,
 * смена props) не должен удваивать отправку.
 */
let started = false;

const startReporting = ({
  collectorProxyUrl,
  siteEnvironment,
  siteBrand,
}: {
  collectorProxyUrl: string;
  siteEnvironment: string;
  siteBrand: string;
}): void => {
  const client = createCollectorProxyBrowserClient({
    baseUrl: collectorProxyUrl,
    appId: {
      service: `site-${siteBrand}`,
      version: "1.0.0",
      environment: siteEnvironment,
      platform: "web",
    },
    // Недоступный коллектор, CORS, блокировщик рекламы — не ошибка сайта:
    // без onError SDK пишет console.error на каждую метрику.
    onError: () => undefined,
  });

  const scenarioRunId = uuidV4();

  const report = (metric: Metric): void => {
    try {
      // CLS — безразмерный score, не миллисекунды: durationMs не проставляем,
      // само значение — в attributes.value.
      const durationMs =
        metric.name === "CLS"
          ? undefined
          : Math.max(0, Math.round(metric.value));

      client.sendAnalyticsEvent({
        event: {
          eventId: uuidV4(),
          timestamp: new Date().toISOString(),
          scenario: { scenarioId: SCENARIO_ID, scenarioRunId },
          name: `site.web-vitals.${metric.name.toLowerCase()}.finished`,
          // Измерение, а не операция: плохая метрика — не сбой. Оценка — в
          // attributes.rating (good / needs-improvement / poor); статус
          // error без error.code нарушал контракт событий observability 2.0.
          status: "ok",
          durationMs,
          attributes: {
            metric: metric.name,
            value: metric.value,
            delta: metric.delta,
            rating: metric.rating,
            // id экземпляра метрики: INP/CLS отправляются повторно при
            // каждом скрытии страницы — по нему берётся последнее значение.
            metric_id: metric.id,
            page_path: window.location.pathname,
            navigation_type: metric.navigationType,
          },
        },
      });
    } catch {
      // Наблюдаемость не имеет права ломать страницу.
    }
  };

  onLCP(report);
  onCLS(report);
  onINP(report);
  onFCP(report);
  onTTFB(report);
};

/**
 * Репортит Core Web Vitals (LCP, CLS, INP, FCP, TTFB) реальных пользователей
 * напрямую в collector-proxy-service через browser-sdk (POST /events/send).
 *
 * Изолирован от страницы:
 *  - запускается после гидрации, в простое браузера (наблюдатели web-vitals
 *    buffered — ранние LCP/FCP/клики не теряются);
 *  - любые исключения SDK/web-vitals перехватываются здесь и не доходят до
 *    ErrorBoundary роута;
 *  - запускается один раз на документ.
 *
 * Требование к инфраструктуре: CORS на collector-proxy-service должен
 * разрешать домен(ы) сайта, иначе метрики не дойдут (сайт при этом работает).
 */
export const WebVitalsReporter = ({
  collectorProxyUrl,
  skipReporting,
  siteEnvironment,
  siteBrand,
}: WebVitalsReporterProps): null => {
  useEffect(() => {
    if (started || skipReporting || !collectorProxyUrl) return;
    started = true;

    runWhenIdle(() => {
      try {
        startReporting({ collectorProxyUrl, siteEnvironment, siteBrand });
      } catch {
        // Наблюдаемость не имеет права ломать страницу.
      }
    });
  }, [collectorProxyUrl, skipReporting, siteEnvironment, siteBrand]);

  return null;
};
